import React, { useEffect, useState } from 'react';

import CurrencyFormField from '@components/form-fields/CurrencyFormField';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { useAppSelector } from '@core/redux/hooks';
import {
  useValidateVignette,
  useValidateVignetteExist
} from '@features/production/policy/policy.slice';
import { filterMotorBodyTypesByLine } from '@features/production/proposal/helpers';
import {
  useGetMotorBodyLovs,
  useGetMotorModelLovs
} from '@features/production/proposal/proposal.slice';
import { useWidgetStyles } from '@features/production/proposal/styles';
import { lookupListAsRecordObject } from '@utils/graph-utils';

import { FormValues } from '../types';

import { FormikErrors, useFormikContext } from 'formik';

type MotorLovs = {
  plateCodeList: Record<string, string>;
  usageTypeList: Record<string, string>;
  bodyTypeList: Record<string, string>;
  engineTypeList: Record<string, string>;
  makeList: Record<string, string>;
  modelList: Record<string, string>;
  colorList: Record<string, string>;
};

type Props = {
  disabledForm: boolean;
  lineCode: string;
  lovs: MotorLovs;
  mainBusinessPartnerID: string;
  currency: string;
  policyID: string;
  translateNameOnLicense: (
    enTxt: string,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<FormValues>>
  ) => Promise<void>;
};

const MotorWidgetAtom: React.FC<Props> = ({
  disabledForm,
  lineCode,
  lovs,
  mainBusinessPartnerID,
  currency,
  policyID,
  translateNameOnLicense
}) => {
  const { classes } = useWidgetStyles();
  const user = useAppSelector((state) => state.user);

  const [getModelLovs] = useGetMotorModelLovs();
  const [getBodyLovs] = useGetMotorBodyLovs();

  const [validateVignette] = useValidateVignette();
  const [validateVignetteExist] = useValidateVignetteExist();
  const [modelLovs, setModelLovs] = useState<Record<string, string>>();
  const [bodyLovs, setBodyLovs] = useState<Record<string, string>>();

  const { values, errors, touched, setFieldValue, handleBlur, setErrors } =
    useFormikContext<FormValues>();

  const motorDetails = values.motorDetails;
  const [vignetteError, setVignetteError] = useState<string | undefined>();

  const validateVignetteOnBlur = async () => {
    try {
      const vignetteValue = values.motorDetails.vignette;
      const vignetteCodeValue = values.motorDetails.vignetteCode;

      if (!vignetteValue || !vignetteCodeValue) return;
      setVignetteError(undefined);

      if (user.info.access === 'POS') {
        const res1 = await validateVignette({
          variables: {
            input: {
              mainBusinessPartnerID: mainBusinessPartnerID,
              vignette: vignetteValue,
              vignetteCode: vignetteCodeValue
            }
          }
        });

        if (res1?.data?.production?.validateVignette?.length === 0) {
          setVignetteError('Vignette Number is not assigned to you');

          return;
        }
      }

      const res2 = await validateVignetteExist({
        variables: {
          input: {
            proposalID: policyID,
            vignette: vignetteValue,
            vignetteCode: vignetteCodeValue
          }
        }
      });

      if (res2?.data?.production?.validateProposalVignette?.length !== 0) {
        setVignetteError('Vignette already in use');

        return;
      }

      setVignetteError(undefined);
    } catch (err) {
      setVignetteError('Error validating vignette number');
    }
  };

  useEffect(() => {
    if (vignetteError && !values.motorDetails.vignette)
      setVignetteError(undefined);
  }, [values.motorDetails.vignette, values.motorDetails.vignetteCode]);

  const fetchModelLovs = async (v: string) => {
    const result = await getModelLovs({ variables: { makeID: v } });
    const lovs = lookupListAsRecordObject(result?.data.core.getModelList ?? []);
    setModelLovs(lovs);
  };

  const fetchBodyLovs = async (v: string) => {
    const result = await getBodyLovs({ variables: { usageType: v } });
    const filteredBodyTypes = filterMotorBodyTypesByLine(
      result?.data.core.getBodyTypeList ?? [],
      lineCode
    );
    const nextBodyLovs = lookupListAsRecordObject(filteredBodyTypes);
    setBodyLovs(nextBodyLovs);

    return nextBodyLovs;
  };

  useEffect(() => {
    if (motorDetails.make) {
      fetchModelLovs(motorDetails.make);
    }
  }, [motorDetails.make]);

  useEffect(() => {
    if (motorDetails.usage) {
      fetchBodyLovs(motorDetails.usage).then((nextBodyLovs) => {
        if (motorDetails?.body && !nextBodyLovs[motorDetails.body]) {
          setFieldValue('motorDetails.body', '');
        }
      });
    }
  }, [motorDetails.usage, lineCode]);

  if (!['4', '50', '51'].includes(lineCode)) {
    return null;
  }

  return (
    <WidgetPaper>
      <WidgetSection title="Motor Details">
        <div className={classes.fieldRow}>
          <TextInputFormField
            key="nameOnLicenseEN"
            name="motorDetails.nameOnLicenseEN"
            title="Name on License (EN)*"
            placeholder="Name on License (EN)"
            value={motorDetails?.nameOnLicenseEN}
            error={
              touched?.motorDetails?.nameOnLicenseEN
                ? errors?.motorDetails?.nameOnLicenseEN
                : ''
            }
            onBlur={async (e) => {
              handleBlur(e);
              const value = e.target.value?.trim();
              if (value) {
                try {
                  await translateNameOnLicense(value, setFieldValue);
                } catch (error) {
                  console.error('Translation failed:', error);
                }
              }
            }}
            onChange={(e) =>
              setFieldValue('motorDetails.nameOnLicenseEN', e.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            key="nameOnLicenseAR"
            name="motorDetails.nameOnLicenseAR"
            title="Name on License (AR)*"
            placeholder="Name on License (AR)"
            value={motorDetails?.nameOnLicenseAR}
            error={
              touched?.motorDetails?.nameOnLicenseAR
                ? errors?.motorDetails?.nameOnLicenseAR
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('motorDetails.nameOnLicenseAR', e.target.value)
            }
            disabled={disabledForm}
            material
          />
          {lineCode === '50' && (
            <TextInputFormField
              key="vignetteCode"
              name="motorDetails.vignetteCode"
              title="Vignette Code*"
              placeholder="Vignette Code"
              value={motorDetails?.vignetteCode}
              error={
                touched?.motorDetails?.vignetteCode
                  ? errors?.motorDetails?.vignetteCode
                  : ''
              }
              onBlur={async (e) => {
                handleBlur(e);
                await validateVignetteOnBlur();
              }}
              onChange={(e) =>
                setFieldValue('motorDetails.vignetteCode', e.target.value)
              }
              disabled={disabledForm}
              material
            />
          )}

          {lineCode === '50' && (
            <NumberInputFormField
              key="vignette"
              name="motorDetails.vignette"
              title="Vignette*"
              placeholder="Vignette"
              value={motorDetails?.vignette}
              error={
                vignetteError ||
                (touched?.motorDetails?.vignette
                  ? errors?.motorDetails?.vignette
                  : '')
              }
              onBlur={async (e) => {
                handleBlur(e);
                await validateVignetteOnBlur();
              }}
              onChange={(e) =>
                setFieldValue(
                  'motorDetails.vignette',
                  e.target.value ? parseInt(e.target.value) : ''
                )
              }
              disabled={disabledForm}
              material
            />
          )}
        </div>
      </WidgetSection>

      <WidgetSection title="Vehicle Details" narrowSpacing hideHeaderBorder>
        <div className={classes.fieldRow}>
          <SelectFormField
            name="motorDetails.plateCode"
            title="Plate Code"
            placeholder="Plate Code"
            value={motorDetails?.plateCode}
            error={
              touched?.motorDetails?.plateCode
                ? errors?.motorDetails?.plateCode
                : ''
            }
            onBlur={async (_, e) => {
              handleBlur(e);
            }}
            onChange={(v) => setFieldValue('motorDetails.plateCode', v)}
            disabled={disabledForm}
            selectOptions={lovs.plateCodeList}
            canClearValue
            material
          />

          <TextInputFormField
            key="plateNumber"
            name="motorDetails.plateNumber"
            title="Plate Number"
            placeholder="Plate Number"
            value={motorDetails?.plateNumber}
            error={
              touched?.motorDetails?.plateNumber
                ? errors?.motorDetails?.plateNumber
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('motorDetails.plateNumber', e.target.value)
            }
            disabled={disabledForm}
            material
          />

          <SelectFormField
            name="motorDetails.usage"
            title="Usage*"
            placeholder="Usage"
            value={motorDetails?.usage}
            error={
              touched?.motorDetails?.usage ? errors?.motorDetails?.usage : ''
            }
            onBlur={async (_, e) => {
              handleBlur(e);
            }}
            onChange={(v) => {
              setFieldValue('motorDetails.usage', v);
              setFieldValue('motorDetails.body', '');
            }}
            disabled={disabledForm}
            selectOptions={lovs.usageTypeList}
            material
          />

          <SelectFormField
            name="motorDetails.body"
            title="Body*"
            placeholder="Body"
            value={motorDetails?.body}
            error={
              touched?.motorDetails?.body ? errors?.motorDetails?.body : ''
            }
            onBlur={async (_, e) => {
              handleBlur(e);
            }}
            onChange={(v) => setFieldValue('motorDetails.body', v)}
            disabled={disabledForm}
            selectOptions={bodyLovs || lovs.bodyTypeList}
            material
          />

          <SelectFormField
            name="motorDetails.engineType"
            title="Engine Type*"
            placeholder="Engine Type"
            value={motorDetails?.engineType}
            error={
              touched?.motorDetails?.engineType
                ? errors?.motorDetails?.engineType
                : ''
            }
            onBlur={async (_, e) => {
              handleBlur(e);
            }}
            onChange={(v) => setFieldValue('motorDetails.engineType', v)}
            disabled={disabledForm}
            selectOptions={lovs.engineTypeList}
            material
          />

          <SelectFormField
            name="motorDetails.make"
            title="Make*"
            placeholder="Make"
            value={motorDetails?.make}
            error={
              touched?.motorDetails?.make ? errors?.motorDetails?.make : ''
            }
            onBlur={async (_, e) => {
              handleBlur(e);
            }}
            onChange={(v) => {
              setFieldValue('motorDetails.make', v);
              setFieldValue('motorDetails.model', '');
            }}
            disabled={disabledForm}
            selectOptions={lovs.makeList}
            material
          />

          <SelectFormField
            name="motorDetails.model"
            title="Model*"
            placeholder="Model"
            value={motorDetails?.model}
            error={
              touched?.motorDetails?.model ? errors?.motorDetails?.model : ''
            }
            onBlur={async (_, e) => {
              handleBlur(e);
            }}
            onChange={(v) => setFieldValue('motorDetails.model', v)}
            disabled={disabledForm}
            selectOptions={modelLovs || lovs.modelList}
            material
          />

          <SelectFormField
            name="motorDetails.color"
            title="Color"
            placeholder="Color"
            value={motorDetails?.color}
            error={
              touched?.motorDetails?.color ? errors?.motorDetails?.color : ''
            }
            onBlur={async (_, e) => {
              handleBlur(e);
            }}
            onChange={(v) => setFieldValue('motorDetails.color', v)}
            disabled={disabledForm}
            selectOptions={lovs.colorList}
            material
          />

          <NumberInputFormField
            key="yearOfMake"
            name="motorDetails.yearOfMake"
            title="Year of Make*"
            placeholder="Year of Make"
            value={motorDetails?.yearOfMake}
            error={
              touched?.motorDetails?.yearOfMake
                ? errors?.motorDetails?.yearOfMake
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue(
                'motorDetails.yearOfMake',
                e.target.value ? parseInt(e.target.value) : ''
              )
            }
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="engine"
            name="motorDetails.engine"
            title="Engine*"
            placeholder="Engine"
            value={motorDetails?.engine}
            error={
              touched?.motorDetails?.engine ? errors?.motorDetails?.engine : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('motorDetails.engine', e.target.value)
            }
            disabled={disabledForm}
            material
          />

          {['VAN', 'PICKUP', 'F_TANKER'].includes(
            values.motorDetails.body ?? ''
          ) && (
            <NumberInputFormField
              key="weight"
              name="motorDetails.weight"
              title="Weight (in Tons)*"
              placeholder="Weight"
              value={motorDetails?.weight}
              error={
                touched?.motorDetails?.weight
                  ? errors?.motorDetails?.weight
                  : ''
              }
              onBlur={(e) => handleBlur(e)}
              onChange={(e) =>
                setFieldValue('motorDetails.weight', e.target.value)
              }
              disabled={disabledForm}
              material
              maxDecimalPrecision={3}
            />
          )}

          {values.motorDetails.body === 'MOTORCYCLE' && (
            <NumberInputFormField
              key="engineSize"
              name="motorDetails.engineSize"
              title="Engine Size (in cc)*"
              placeholder="Engine Size"
              value={motorDetails?.engineSize}
              error={
                touched?.motorDetails?.engineSize
                  ? errors?.motorDetails?.engineSize
                  : ''
              }
              onBlur={(e) => handleBlur(e)}
              onChange={(e) =>
                setFieldValue('motorDetails.engineSize', e.target.value)
              }
              disabled={disabledForm}
              material
              maxDecimalPrecision={3}
            />
          )}

          <NumberInputFormField
            key="seatingCapacity"
            name="motorDetails.seatingCapacity"
            title="Seating Capacity*"
            placeholder="Seating Capacity"
            value={motorDetails?.seatingCapacity}
            error={
              touched?.motorDetails?.seatingCapacity
                ? errors?.motorDetails?.seatingCapacity
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue(
                'motorDetails.seatingCapacity',
                e.target.value ? parseInt(e.target.value) : ''
              )
            }
            disabled={disabledForm}
            material
          />

          <TextInputFormField
            key="chassis"
            name="motorDetails.chassis"
            title="Chassis*"
            placeholder="Chassis"
            value={motorDetails?.chassis}
            error={
              touched?.motorDetails?.chassis
                ? errors?.motorDetails?.chassis
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('motorDetails.chassis', e.target.value)
            }
            disabled={disabledForm}
            material
          />

          <NumberInputFormField
            key="horsePower"
            name="motorDetails.horsePower"
            title="Horse Power*"
            placeholder="Horse Power"
            value={motorDetails?.horsePower}
            error={
              touched?.motorDetails?.horsePower
                ? errors?.motorDetails?.horsePower
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue(
                'motorDetails.horsePower',
                e.target.value ? parseInt(e.target.value) : ''
              )
            }
            disabled={disabledForm}
            material
          />

          {lineCode === '51' && (
            <>
              <CurrencyFormField
                key="carValue"
                name="motorDetails.carValue"
                title="Car Value*"
                placeholder="Car Value"
                value={motorDetails?.carValue}
                error={
                  touched?.motorDetails?.carValue
                    ? errors?.motorDetails?.carValue
                    : ''
                }
                onBlur={(e) => handleBlur(e)}
                onChange={(e) =>
                  setFieldValue('motorDetails.carValue', e.target.value)
                }
                disabled={disabledForm}
                material
                currencySymbol={currency}
                maxDecimalPrecision={0}
              />

              <NumberInputFormField
                key="agencyRepairYears"
                name="motorDetails.agencyRepairYears"
                title="Agency Repair Years"
                placeholder="Agency Repair Years"
                value={motorDetails?.agencyRepairYears}
                error={
                  touched?.motorDetails?.agencyRepairYears
                    ? errors?.motorDetails?.agencyRepairYears
                    : ''
                }
                onBlur={(e) => handleBlur(e)}
                onChange={(e) =>
                  setFieldValue(
                    'motorDetails.agencyRepairYears',
                    e.target.value ? parseInt(e.target.value) : ''
                  )
                }
                disabled={disabledForm}
                material
              />

              <NumberInputFormField
                key="depreciationYears"
                name="motorDetails.depreciationYears"
                title="Depreciation Years"
                placeholder="Depreciation Years"
                value={motorDetails?.depreciationYears}
                error={
                  touched?.motorDetails?.depreciationYears
                    ? errors?.motorDetails?.depreciationYears
                    : ''
                }
                onBlur={(e) => handleBlur(e)}
                onChange={(e) =>
                  setFieldValue(
                    'motorDetails.depreciationYears',
                    e.target.value ? parseInt(e.target.value) : ''
                  )
                }
                disabled={disabledForm}
                material
              />
            </>
          )}
        </div>
      </WidgetSection>
    </WidgetPaper>
  );
};

export { MotorWidgetAtom };
